import { get } from "lodash";
import React, { Component } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Container, Header, Icon } from "semantic-ui-react";

import { buildAtId } from "../../utils/utils.js";

import Entity404 from "../../components/entity404/Entitiy404.jsx";
import SimplePreloader from "../../components/preloaders/SimplePreloader.jsx";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs.jsx";
import ActionDropdownMenu from "../../components/actionDropdownMenu/ActionDropdownMenu.jsx";
import UsagePanel from "../../components/usage/UsagePanel.jsx";
import AttachedSchedulesPanel from "../../components/resources/AttachedSchedulesPanel.jsx";
import ResourceDetailsPanel from "../../components/resources/resourceDetailsPanel/ResourceDetailsPanel.jsx";
import ResourceMarker from "../../components/marking/ResourceMarker.jsx";
import MarkingMessage from "../../components/marking/MarkingMessage.jsx";
import UserPanel from "../../components/users/UserPanel.jsx";
import StartStopPowerIconControl from "../../components/resources/startStopControl/StartStopPowerIconControl.jsx";

import withConfirmationModal from "../../components/modals/withConfirmationModal.js";
import { Media } from "../../styles/Media.js";

import { getIconByType } from "../../components/resources/resourceIcon/ResourceIconMap.js";
import { getDisabledStateFromMarking } from "../../utils/marking.js";
import { getResource } from "../../state/reducers/resources/resourcesActions.js";
import { operationActionByEntity } from "../../state/reducers/operations/operationActions.js";
import { operationAction } from "/state/reducers/operations/operationActions.js";

import { OPERATIONS } from "../../constants/operations.js";
import { RESOURCES } from "../../state/actions/actionConstants.js";
import {
  ACTION_URI_CHUNKS,
  RESOURCE_URI_CHUNKS
} from "../../constants/entity.js";
import { ROUTES } from "../../constants/routing.js";

import resourceSelector from "../../state/reducers/resources/resourceSelector.js";
import { selectLatestErrorMessage } from "../../state/reducers/api/apiSelectors.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Resource extends Component {
  static propTypes = {
    resource: PropTypes.object,
    permissions: PropTypes.object.isRequired,
    isErrored: PropTypes.bool,
    getResource: PropTypes.func.isRequired,
    operationActionByEntity: PropTypes.func.isRequired,
    copy: PropTypes.func.isRequired,

    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    this.state = {
      resourceId: buildAtId(props.match.params),
      resourceType: props.match.params.type,
      handleDelete: null,
      markingData: null,
      marking: null
    };
  }

  static getDerivedStateFromProps(props, state) {
    const marking = get(props, "resource.marking", null);

    if (marking !== state.marking) {
      const markingData = getDisabledStateFromMarking(marking);
      return { marking, markingData };
    }

    return null;
  }

  componentDidMount = () => {
    const { match, getResource } = this.props;
    getResource(match.params.id, match.params.type, true, true, true);
  };

  // -- HANDLERS -- //

  handleStartStopClick = (id, name) => {
    const { resource, operationActionByEntity } = this.props;
    operationActionByEntity(resource, name);
  };

  handleDelete = () => {
    const { resource } = this.props;
    this.props.openModal(
      this.doDelete,
      "resource.modals.delete_modal",
      undefined,
      resource
    );
  };

  doDelete = () => {
    const { resource, operationActionByEntity } = this.props;
    operationActionByEntity(resource, OPERATIONS.DELETE);
  };

  doEdit = values => {
    const { resource, operationActionByEntity } = this.props;
    operationActionByEntity(resource, OPERATIONS.UPDATE, values);
  };

  // -- RENDER -- //

  getProjectSection = () => {
    const { resource } = this.props;

    const project = resource.project;

    if (!project || typeof project === "string") return {};

    return {
      content: get(project, "name", "Unknown"),
      href: get(project, "@id", "")
    };
  };

  getBreadcrumbSections = () => {
    const { copy, resource } = this.props;

    return [
      { content: copy("navigation.projects"), href: ROUTES.PROJECTS },
      this.getProjectSection(),
      { content: get(resource, "name", "Unknown"), active: true }
    ];
  };

  renderResourceIcon = type => {
    const { markingData } = this.state;

    const classNameStr =
      markingData.functions || markingData.views
        ? "large-resource-icon clear-both"
        : "large-resource-icon";

    return (
      <Icon
        name={getIconByType(type)}
        size="massive"
        className={classNameStr}
        disabled={markingData.functions}
      />
    );
  };

  renderStartStopControl = data => {
    const { permissions, copy } = this.props;
    const { markingData } = this.state;

    return (
      <StartStopPowerIconControl
        data={data}
        permissions={permissions}
        copy={copy("resource.start_stop_button")}
        className="resource-start-stop-button"
        onClick={this.handleStartStopClick}
        disabled={markingData.functions}
      />
    );
  };

  render() {
    const { resource, isFetching, isErrored, errorMessage, copy } = this.props;

    const { resourceId, resourceType, markingData } = this.state;

    // early return if resource isn't ready to render
    if (isFetching) {
      return <SimplePreloader />;
    }

    if (!resource || !resource["@id"]) {
      return (
        <Entity404
          id={resourceId}
          type={copy(`resource_types.singular.${resourceType.toLowerCase()}`)}
        />
      );
    }

    const createImagePath = `/${RESOURCE_URI_CHUNKS.IMAGES}/${ACTION_URI_CHUNKS.CREATE}${resource["@id"]}`;

    return (
      <Container>
        <Breadcrumbs sections={this.getBreadcrumbSections()} />

        <ActionDropdownMenu
          onDelete={this.handleDelete}
          data={resource}
          createImagePath={createImagePath}
          copy={copy("basic_resource_actions")}
        />
        <Header as="h3" className="action-dropdown-header">
          {get(resource, "name", "Unknown")}
        </Header>

        <ResourceMarker marking={resource.marking} className="project-marker" />

        {this.renderStartStopControl(resource)}

        <Media at="mobile">{this.renderResourceIcon(resource["@type"])}</Media>

        <MarkingMessage
          type={MarkingMessage.RESOURCE}
          message={markingData.message}
        />

        <ResourceDetailsPanel
          data={resource}
          doEdit={this.doEdit}
          disabled={false}
        />

        <UserPanel
          data={resource}
          isErrored={isErrored}
          errorMessage={errorMessage}
          disabled={markingData.views || false}
        />

        <UsagePanel data={resource} disabled={false} />

        {resource.planners && (
          <AttachedSchedulesPanel
            data={resource.planners}
            disabled={markingData.views}
          />
        )}
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  resource: resourceSelector.selectResource()(state, props),
  permissions: state.account.permissions,
  isFetching: resourceSelector.selectIsFetching()(state, props),
  isErrored: state.api.isError,
  errorMessage: selectLatestErrorMessage(state),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getResource: (id, type, charge, usage, mesurements) =>
    dispatch(getResource(id, type, charge, usage, mesurements)),
  operationActionByEntity: (...args) =>
    dispatch(operationActionByEntity(...args)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(RESOURCES, entity, operationName, data, options))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withConfirmationModal(Resource));
